import { useState, useRef } from 'react';
import api from '../services/api';

const ALLOWED = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.kmz,.kml,.shp,.dwg,.zip';
const ALLOWED_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'kmz', 'kml', 'shp', 'dwg', 'zip'];

export default function UploadModal({ uploadUrl, title, subtitle, heading = 'Upload Attachments', onClose, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  function addFiles(list) {
    const valid = Array.from(list).filter((f) => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ALLOWED_EXTS.includes(ext);
    });
    const rejected = list.length - valid.length;
    if (rejected > 0) setError(`${rejected} file(s) skipped — unsupported type`);
    setFiles((prev) => [...prev, ...valid]);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }

  function handleFileInput(e) {
    addFiles(e.target.files);
    e.target.value = '';
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    if (!files.length) {
      setError('Please add at least one file');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      await api.post(uploadUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
      onClose();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg rounded-xl border border-navy-500 p-6" style={{ backgroundColor: '#142236' }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-white font-bold text-lg">{heading}</h2>
            <p className="text-gray-400 text-xs mt-0.5 font-mono">{title}{subtitle ? ` — ${subtitle}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
        </div>

        {/* Drag & Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition mb-4 ${
            dragging ? 'border-gold-500 bg-navy-600' : 'border-navy-500 hover:border-gold-500'
          }`}
        >
          <p className="text-gray-400 text-sm mb-1">Drag & drop files here, or click to browse</p>
          <p className="text-gray-500 text-xs">PDF, DWG, ZIP, KMZ, images, shapefiles</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ALLOWED}
            className="hidden"
            onChange={handleFileInput}
          />
        </div>

        {/* File List */}
        {files.length > 0 && (
          <ul className="space-y-2 mb-4 max-h-60 overflow-y-auto">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between rounded px-3 py-2" style={{ backgroundColor: '#0f1a29' }}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded shrink-0" style={{ color: '#c9a84c', backgroundColor: '#1e3a5f' }}>
                    {f.name.split('.').pop()}
                  </span>
                  <span className="text-white text-sm truncate">{f.name}</span>
                </div>
                <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400 transition text-sm shrink-0 ml-3">
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={uploading || !files.length}
            className="flex-1 py-2 rounded font-bold uppercase tracking-wide text-sm disabled:opacity-50 transition"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            {uploading ? 'Uploading…' : `Upload ${files.length || ''} File${files.length === 1 ? '' : 's'}`}
          </button>
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 rounded border border-navy-500 text-gray-400 text-sm hover:text-white hover:border-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
